import React, {Component} from "react";
import {LexoRank} from "lexorank";
import CustomIcon from "../UI-components/CustomIcon";
import CustomInput from "../UI-components/CustomInput";
import Tooltip from "../UI-components/Tooltip";
import {DragDropContainer, DropTarget} from "react-drag-drop-container";
import {
    formatNumber,
    formatStatusLabel
} from "../../services/Api";
import PopupLia from "./Cluster-Lia/PopupLia";
import PopupClusterClassification from "./Cluster-Lia/PopupClusterClassification";
import Badge from "../UI-components/Badge";
import ModuleJar from "./ModuleJar/ModuleJar";
import ReactDragListView from "react-drag-listview/lib/index.js";
import Popup from "../UI-components/Popup";

class ModuleListAreaContainer extends Component {
    state = {
        isBinOpen: false,
        localModules: [],
        rankingChanges: {},
        recentlyMoved: new Set(),
        isAlignPopupOpen: false,
    };

    _movedTimer = null;
    _rowRefs = {};

    componentWillUnmount() {
        clearTimeout(this._movedTimer);
    }

    capturePositions = () => {
        const positions = {};
        Object.entries(this._rowRefs).forEach(([name, el]) => {
            if (el) positions[name] = el.getBoundingClientRect().top;
        });
        return positions;
    };

    animateFlip = (prevPositions) => {
        requestAnimationFrame(() => {
            const animated = [];
            Object.entries(this._rowRefs).forEach(([name, el]) => {
                if (!el || prevPositions[name] === undefined) return;
                const delta = prevPositions[name] - el.getBoundingClientRect().top;
                if (Math.abs(delta) < 1) return;
                el.style.transition = 'none';
                el.style.transform = `translateY(${delta}px)`;
                animated.push(el);
            });
            if (animated.length === 0) return;
            requestAnimationFrame(() => {
                animated.forEach(el => {
                    el.style.transition = 'transform 0.35s ease';
                    el.style.transform = '';
                });
                animated[0].addEventListener('transitionend', () => {
                    animated.forEach(el => {
                        el.style.transition = '';
                        el.style.transform = '';
                    });
                }, {once: true});
            });
        });
    };

    flashMoved = (names) => {
        clearTimeout(this._movedTimer);
        this.setState({recentlyMoved: new Set(names)});
        this._movedTimer = setTimeout(() => this.setState({recentlyMoved: new Set()}), 650);
    };

    componentDidMount() {
        const flatModules = Object.values(this.props.listModule).flat();
        this.setState({localModules: this.withInitialPositions(flatModules)});
    }

    componentDidUpdate(prevProps) {
        // After a successful save: reorder by rankingPosition
        if (this.props.isActionSuccessful && !prevProps.isActionSuccessful) {
            const flatModules = Object.values(this.props.listModule).flat();
            const sorted = this.withSavedPositions(flatModules);
            const resetPositions = sorted.map((m, i) => ({ ...m, computedPosition: i + 1 }));
            this.setState({
                localModules: resetPositions,
                rankingChanges: {}
            });
            return;
        }

        // When listModule changes (module added, restored, or removed)
        if (prevProps.listModule !== this.props.listModule) {
            const newFlatModules = Object.values(this.props.listModule).flat();
            const currentNames = new Set(this.state.localModules.map(m => m.moduleName));
            const newNames = new Set(newFlatModules.map(m => m.moduleName));

            const hasAddedOrRestored = newFlatModules.some(m => !currentNames.has(m.moduleName));
            const hasRemoved = this.state.localModules.some(m => !newNames.has(m.moduleName));

            if (!hasAddedOrRestored && !hasRemoved) {
                // No modules added/removed: only data updates (e.g. status)
                // Update data while preserving local order and rankingPositions
                const updated = this.state.localModules.map(m => {
                    const fresh = newFlatModules.find(nm => nm.moduleName === m.moduleName);
                    return fresh
                        ? { ...fresh, rankingPosition: m.rankingPosition, computedPosition: m.computedPosition }
                        : m;
                });
                this.setState({ localModules: updated });
                return;
            }

            if (Object.keys(this.state.rankingChanges).length === 0 && !hasAddedOrRestored) {
                // No manual reorder and no additions: clean reset
                // (e.g. initial load after cluster change)
                this.setState({ localModules: this.withInitialPositions(newFlatModules) });
                return;
            }

            // General case: merge and keep the FE order aligned with rankingPosition
            const updatedExisting = this.state.localModules
                .filter(m => newNames.has(m.moduleName))
                .map(m => {
                    const fresh = newFlatModules.find(nm => nm.moduleName === m.moduleName);
                    return fresh
                        ? { ...fresh, rankingPosition: m.rankingPosition, computedPosition: m.computedPosition }
                        : m;
                });

            // Genuinely new modules (added or restored): place them in ranking order
            const addedOrRestored = newFlatModules.filter(m => !currentNames.has(m.moduleName));
            const merged = this.withSavedPositions([...updatedExisting, ...addedOrRestored]);
            this.setState({ localModules: merged });
        }
    }

    withSavedPositions = (modules) =>
        [...modules].sort((a, b) => {
            const ra = a.rankingPosition;
            const rb = b.rankingPosition;

            // Both have rankingPosition: sort lexicographically (LexoRank)
            if (ra && rb) return LexoRank.parse(ra).compareTo(LexoRank.parse(rb));

            // items without rankingPosition go to the bottom
            if (ra && !rb) return -1;
            if (!ra && rb) return 1;

            // Both with no ranking position: sort by moduleName as tiebreaker
            return a.moduleName.localeCompare(b.moduleName);
        });

    withInitialPositions = (modules) =>
        modules.sort((a, b) => (a.computedPosition ?? Infinity) - (b.computedPosition ?? Infinity));

    computeDiscoStats = (filterFun) => {
        let totDisco = 0
        let totSku = 1

        const allModules = []
        Object.keys(this.props.listModule).forEach(family => {
            allModules.push(...this.props.listModule[family])
        })

        totDisco = allModules.map(module => {
            return module?.productDtoList.filter(filterFun).length
        }).reduce((totA, totB) => totA + totB)

        totSku = allModules.map(module => {
            return module?.productDtoList.length
        }).reduce((totA, totB) => totA + totB)

        return ({
            tot: totDisco || '-',
            perc: `${(totDisco / totSku).toFixed(3) * 100}%`
        })
    }

    toggleBin = () =>
        this.setState((prevState) => {
            return {isBinOpen: !prevState.isBinOpen};
        });

    onDragStart = (moduleI) => {
        this.props.dragStart(moduleI);
    };

    onDragEnter = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        let areaDrop = document.querySelector(`.modulePrinc`);
        areaDrop.classList.add("dragOver");
    };

    onDragLeave = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        let areaDrop = document.querySelector(`.modulePrinc`);
        areaDrop.classList.remove("dragOver");
    };

    onDragOver = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
    };

    onDrop = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();

        let areaDrop = document.querySelector(`.modulePrinc`);
        areaDrop.classList.remove("dragStart", "dragOver");
        this.props.dropModule();
        this.props.dragStop();
    };

    assignRanksToAffected = (reorderedList, maxAffectedIndex, currentChanges, movedNames) => {
        const updatedList = [...reorderedList];
        const newChanges = {...currentChanges};

        for (let i = 0; i <= maxAffectedIndex; i++) {
            const currentItem = updatedList[i];
            const isMovedItem = movedNames.has(currentItem.moduleName);
            const needsRank = !currentItem.rankingPosition || isMovedItem;

            if (needsRank) {
                const prevRank = i === 0
                    ? LexoRank.min()
                    : LexoRank.parse(updatedList[i - 1].rankingPosition);

                let nextRank = LexoRank.max();
                for (let k = i + 1; k < updatedList.length; k++) {
                    if (updatedList[k].rankingPosition && !movedNames.has(updatedList[k].moduleName)) {
                        nextRank = LexoRank.parse(updatedList[k].rankingPosition);
                        break;
                    }
                }

                // If it cannot create a new ranking (it conflict with an already existing one and no prev ranking can be created)
                // then rebalance all ranking in the list
                if (prevRank.compareTo(nextRank) >= 0) {
                    return this.rebalanceAllRanks(reorderedList, currentChanges);
                }

                const newRank = prevRank.between(nextRank).toString();
                updatedList[i] = {...currentItem, rankingPosition: newRank};
                newChanges[currentItem.moduleName] = newRank;
            }
        }

        return {updatedList, newChanges};
    };

    rebalanceAllRanks = (reorderedList, currentChanges) => {
        const updatedList = [...reorderedList];
        const newChanges = {...currentChanges};

        let prev = LexoRank.min();
        const max = LexoRank.max();

        for (let updatedElement of updatedList) {
            const newRank = prev.between(max).toString();
            newChanges[updatedElement.moduleName] = newRank;
            updatedElement = {...updatedElement, rankingPosition: newRank};
            prev = LexoRank.parse(newRank);
        }

        return {updatedList, newChanges};
    };

    handleAlign = () => {
        const moduleNames = this.state.localModules.map(m => m.moduleName);
        this.props.onAlign(moduleNames).then(rankingModules => {
            if (!rankingModules || rankingModules.length === 0) return;

            const rankMap = {};
            const computedPositionMap = {};
            rankingModules.forEach(m => {
                rankMap[m.moduleName] = m.rankingPosition;
                computedPositionMap[m.moduleName] = m.computedPosition;
            });

            const newRankingChanges = {...this.state.rankingChanges};
            const updatedModules = this.state.localModules.map(m => {
                const newRank = rankMap[m.moduleName];
                const newPos = computedPositionMap[m.moduleName];
                const updated = {
                    ...m,
                    rankingPosition: newRank ?? null,
                    computedPosition: newPos ?? m.computedPosition,
                };
                if (newRank) {
                    newRankingChanges[m.moduleName] = newRank;
                } else {
                    delete newRankingChanges[m.moduleName];
                }
                return updated;
            });

            const sortedModules = this.withSavedPositions(updatedModules);

            const finalModules = sortedModules.map((m, i) => ({...m, computedPosition: i + 1}));

            const prevPositions = this.capturePositions();
            this.setState({
                localModules: finalModules,
                rankingChanges: newRankingChanges,
                isAlignPopupOpen: true,
            }, () => {
                this.animateFlip(prevPositions);
                if (this.props.onRankingChange) {
                    this.props.onRankingChange(newRankingChanges, finalModules, {customRanking: false});
                }
            });
            this.flashMoved(Object.keys(newRankingChanges));
        });
    };

    handleDragEnd = (fromIndex, toIndex) => {
        if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;

        const updatedList = [...this.state.localModules];
        const [draggedItem] = updatedList.splice(fromIndex, 1);
        updatedList.splice(toIndex, 0, draggedItem);

        const maxAffectedIndex = Math.max(fromIndex, toIndex);
        const movedNames = new Set([draggedItem.moduleName]);

        const {updatedList: finalList, newChanges} = this.assignRanksToAffected(
            updatedList,
            maxAffectedIndex,
            this.state.rankingChanges,
            movedNames
        );

        const prevPositions = this.capturePositions();
        this.setState({localModules: finalList, rankingChanges: newChanges}, () => {
            this.animateFlip(prevPositions)
            if (this.props.onRankingChange) this.props.onRankingChange(this.state.rankingChanges, this.state.localModules);
        });
        this.flashMoved([draggedItem.moduleName]);
    };

    render() {
        const {localModules, isBinOpen, recentlyMoved} = this.state;
        const isEditMode = !this.props.isDisabled;

        const dragProps = isEditMode
            ? {
                onDragEnd: this.handleDragEnd,
                nodeSelector: ".module-list-row",
                handleSelector: ".drag-handle",
            }
            : {
                onDragEnd: () => {},
                nodeSelector: ".module-list-row",
                handleSelector: ".drag-handle-disabled",
            };

        return (
            <div className="cluster-comp-container">
                <HeaderCluster
                    title={this.props.title}
                    description={this.props.description}
                    toogleDescriptionEditMode={this.props.toogleDescriptionEditMode}
                    handleDescriptionChange={this.props.handleDescriptionChange}
                    status={this.props.status}
                    stickHeader={this.props.stickHeader}
                    badgeText={this.props.badgeText}
                    badgeClassName={this.props.badgeClassName}
                />
                {
                    isBinOpen ? (

                        <div
                            id="removedMod"
                            className={
                                "component half-area module-list " +
                                (!this.props.isAlternativeOpen ? " open" : "")
                            }
                        >
                            <div className="header-half-area">
                                <h2 className="title">Removed Modules</h2>
                                <Tooltip
                                    onClick={this.toggleBin}
                                    tooltipTextClass="white"
                                    tooltipClass="tooltip-cluster-comp left"
                                    text="View Cluster Composition"
                                >
                                    <CustomIcon iconClass="icon-cestino"/>
                                </Tooltip>
                            </div>
                            <div className="container-module-jar">
                                {this.props.listRemoved.length > 0 ? (
                                    this.props.listRemoved.map((moduleI) => (
                                        <DragDropContainer
                                            noDragging={this.props.isDisabled || isBinOpen}
                                            key={moduleI.moduleName}
                                            targetKey="drag-drop"
                                            dragData={moduleI}
                                            onDragStart={() => {}}
                                        >
                                            <ModuleJar
                                                moduleItem={moduleI}
                                                draggable={!this.props.isDisabled}
                                                goToSingleModule={(moduleItem) =>
                                                    this.props.goToSingleModule(moduleItem)
                                                }
                                                isRemoved
                                                addToClusterComp={(moduleI) =>
                                                    this.props.addToClusterComp(moduleI, this.props.cluster.clusterId, this.props.cluster.brandCode)
                                                }
                                            />
                                        </DragDropContainer>
                                    ))
                                ) : (
                                    <p className="msg">There aren't removed modules</p>
                                )}
                            </div>
                        </div>

                    ) : (

                        <DropTarget
                            targetKey="drag-drop"
                            onDragEnter={this.onDragEnter}
                            onDragOver={this.onDragOver}
                            onDragLeave={this.onDragLeave}
                            onHit={this.onDrop}
                        >
                            <div
                                className={
                                    (this.props.dragFrom !== null && this.props.dragFrom !== "princ"
                                        ? " dragStart"
                                        : "") + (!this.props.isAlternativeOpen ? " open" : "") + " modulePrinc "
                                }
                            >
                                <div
                                    className={
                                        "component half-area module-list " +
                                        (!this.props.isAlternativeOpen ? "open" : "")
                                    }
                                >
                                    <div className="header-half-area">
                                        <h2 className="title">Module List</h2>
                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                            <ClusterRankingStatus
                                                syncStatus={this.props.cluster.rankingStatus}
                                                onAlign={this.handleAlign}
                                            />
                                            <Tooltip
                                                onClick={this.toggleBin}
                                                tooltipTextClass="white"
                                                tooltipClass="tooltip-cluster-comp left"
                                                text="View Removed Modules"
                                            >
                                                <CustomIcon iconClass="icon-cestino"/>
                                            </Tooltip>
                                        </div>
                                    </div>

                                    <ReactDragListView {...dragProps}>
                                        <div className="container-module-sortable-list">
                                            {localModules.length > 0 ? (
                                                localModules.map((moduleI, index) => {
                                                    const currentPos = index + 1;
                                                    const originalPos = moduleI.computedPosition || currentPos;
                                                    const diff = originalPos - currentPos;
                                                    const displayIndex = currentPos.toString().padStart(2, "0") + ".";
                                                    const skuCount = moduleI.productDtoList ? moduleI.productDtoList.length : 0;

                                                    return (
                                                        <ModuleRow
                                                            key={moduleI.moduleName}
                                                            moduleItem={moduleI}
                                                            displayIndex={displayIndex}
                                                            diff={diff}
                                                            skuCount={skuCount}
                                                            isChanged={diff !== 0}
                                                            isChangedNegative={diff < 0}
                                                            isRecentlyMoved={recentlyMoved.has(moduleI.moduleName)}
                                                            rowRef={(el) => {
                                                                if (el) this._rowRefs[moduleI.moduleName] = el;
                                                                else delete this._rowRefs[moduleI.moduleName];
                                                            }}
                                                            actionIconClass="icon-cestino"
                                                            onActionClick={(item) => this.props.remove(item, this.props.cluster.clusterId)}
                                                            onSync={() => console.log('Sync Module Alignment', moduleI.moduleName)}
                                                        />
                                                    );
                                                })
                                            ) : (
                                                <p className="empty-cluster">
                                                    Search and drag modules to complete your new cluster
                                                </p>
                                            )}
                                        </div>
                                    </ReactDragListView>
                                </div>
                            </div>
                        </DropTarget>

                    )}

                <FooterClusterStatistics
                    rangeFacing={this.props.rangeFacing}
                    cluster={this.props.cluster}
                    listModule={this.props.listModule}
                    percObj={this.props.percObj}
                    setLiaListEdited={this.props.setLiaListEdited}
                    addClassification={this.props.addClassification}
                    computeTotDiscoAndPerc={this.computeDiscoStats}
                    setClusterLias={this.props.setClusterLias}
                />
                {this.state.isAlignPopupOpen && (
                    <Popup
                        title="Alignment applied"
                        subtitle="The module order has been aligned to the Module Master Ranking. Click Save to persist the changes."
                        close={() => this.setState({isAlignPopupOpen: false})}
                    />
                )}
            </div>
        )
    }
}

const ModuleRow = ({moduleItem, displayIndex, diff, skuCount, isChanged, isChangedNegative, isRecentlyMoved, rowRef, actionIconClass, onActionClick, onSync}) => {
    const diffLabel = isChanged
        ? diff > 0 ? `(+${diff})` : `(${diff})`
        : null;

    return (
        <div ref={rowRef} className={`module-list-row${isChanged ? " row-changed" : ""}${isRecentlyMoved ? " row-just-moved" : ""}`}>
            <div className="row-left">
                <span className="drag-handle">&#9776;</span>
                <span className="module-index">
                    {displayIndex}
                    {diffLabel && (
                        <span className={isChangedNegative ? "pos-changed-negative" : "pos-changed"}>
                            {" "}{diffLabel}
                        </span>
                    )}
                </span>
                <span className="module-name">{moduleItem.moduleLabel}</span>
            </div>
            <div className="row-right">
                <span className="module-sku">{skuCount} SKU</span>
                {actionIconClass && onActionClick && (
                    <div
                        className="delete-action"
                        onClick={(e) => {
                            e.stopPropagation();
                            onActionClick(moduleItem);
                        }}
                    >
                        <CustomIcon iconClass={actionIconClass}/>
                    </div>
                )}
            </div>
        </div>
    );
};

const CLUSTER_STATUS_CONFIG = {
    unsynced: {
        label: "Out of sync — align",
        description: "A Master Ranking update from a later month was selected, which may cause discrepancies. Click to force the module order realignment.",
        icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="15" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="11" y2="18"/>
                <path d="M17 15l3-3 3 3"/><line x1="20" y1="12" x2="20" y2="21"/>
            </svg>
        ),
        style: {
            background: "rgba(210,70,70,0.13)",
            border: "1px solid rgba(210,70,70,0.4)",
            color: "#d47a7a",
        },
        interactive: true,
    },
    custom: {
        label: "Custom order — align",
        description: "This cluster uses a custom module order. Click to restore the standard Master Ranking sequence.",
        icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="18" y2="6"/><line x1="3" y1="12" x2="18" y2="12"/><line x1="3" y1="18" x2="13" y2="18"/>
                <circle cx="19" cy="18" r="2" fill="currentColor" stroke="none"/>
            </svg>
        ),
        style: {
            background: "rgba(210,150,40,0.13)",
            border: "1px solid rgba(210,150,40,0.4)",
            color: "#c9a050",
        },
        interactive: true,
    },
    aligned: {
        label: "Aligned",
        description: "Module order is in sync with the current Master Ranking configuration.",
        icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
            </svg>
        ),
        style: {
            background: "rgba(85,135,50,0.13)",
            border: "1px solid rgba(85,135,50,0.38)",
            color: "#6aaa45",
            cursor: "default",
            pointerEvents: "none",
        },
        interactive: false,
    },
};

const normalizeClusterStatus = (s) => {
    if (!s) return null;
    const u = s.toUpperCase();
    if (u === "ALIGNED" || u === "SYNCHRONIZED") return "aligned";
    if (u === "CUSTOM") return "custom";
    if (u === "NOT_ALIGNED" || u === "UNSYNCHRONIZED") return "unsynced";
    return null;
};

const ClusterRankingStatus = ({syncStatus, onAlign}) => {
    const key = normalizeClusterStatus(syncStatus);
    const config = key ? CLUSTER_STATUS_CONFIG[key] : null;
    if (!config) return null;

    const baseStyle = {
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        borderRadius: "20px",
        padding: "6px 10px",
        fontSize: "14px",
        fontWeight: 500,
        ...config.style,
    };

    return (
        <Tooltip
            tooltipTextClass="white"
            tooltipClass="tooltip-cluster-comp bottom-right"
            text={config.description}
        >
            <div
                style={baseStyle}
                className={`cluster-ranking-status${config.interactive ? " is-clickable" : ""}`}
                onClick={config.interactive ? (e) => { e.stopPropagation(); onAlign(); } : undefined}
            >
                <span style={{display: "flex", alignItems: "center"}}>{config.icon}</span>
                <span>{config.label}</span>
            </div>
        </Tooltip>
    );
};

const HeaderCluster = (props) => {

    return (
        <div style={{
            paddingTop: "10px",
            paddingBottom: "5px"
        }}>
            <div style={{
                className: "header-cluster",
                display: 'flex',
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0.5rem",
                backgroundColor: "#e5e5e5",
                width: props.stickHeader ? "100%" : "auto",
                zIndex: 101,
                position: props.stickHeader ? "sticky" : "unset",
                top: 0,

            }}>
                <p style={{display: 'flex', alignItems: 'center', position: 'relative', paddingRight: '0.5rem'}}>
                    {props.title}
                    {props.badgeText && (
                        <Badge
                            text={props.badgeText}
                            className={props.badgeClassName}
                        />
                    )}
                </p>
                <div className="mc-status">
                    {formatStatusLabel(props.status)}
                </div>
            </div>
            <div>
                {
                    props.description.editMode || !props.description.value ?
                        <CustomInput
                            handleChange={props.handleDescriptionChange}
                            value={props.description.value}
                            placeholder="Cluster Description"
                            maxLength="100"
                        ></CustomInput> :
                        <p style={{padding: "15px 0", color: "grey", fontStyle: "italic", cursor: "text"}}
                           onClick={props.toogleDescriptionEditMode}>{props.description.value}</p>
                }
            </div>
        </div>
    )
}

const FooterClusterStatistics = (props) => {
    return (
        <div className="cluster-footer" style={{
            height: "auto",
            padding: "36px 0"
        }}>
            <div className="box-footer">
                <p>
                    {" "}
                    Range Facing:{" "}
                    <span className="bold">
                        {props.rangeFacing}
                    </span>{" "}
                </p>
                <p>
                    {" "}
                    Door:{" "}
                    <span className="bold">
                        {props.cluster.stores &&
                            formatNumber(props.cluster.stores.length)}
                    </span>{" "}
                </p>
                {props.percObj ? (
                    <p>
                        {" "}
                        Refresh:{" "}
                        <span className="bold">
                            {parseInt(
                                props.percObj.percentageRefresh
                            ).toFixed(0)}
                            %
                        </span>{" "}
                    </p>
                ) : null}

                {
                    /*
                    <>
                    <p>
                        {" "}
                        Tot disco MH:{" "}
                        <span className="bold">
                            {props.listModule &&
                                props.computeTotDiscoAndPerc((sku) => sku.mustHave).tot}
                        </span>{" "}
                    </p>
                    <p>
                        {" "}
                        Tot disco EL:{" "}
                        <span className="bold">
                            {props.listModule &&
                                props.computeTotDiscoAndPerc((sku) => sku.eligible).tot}
                        </span>{" "}
                    </p>
                    <p>
                        {" "}
                        Tot disco MH+EL:{" "}
                        <span className="bold">
                            {props.listModule &&
                                props.computeTotDiscoAndPerc((sku) => sku.eligible || sku.mustHave).tot}
                        </span>{" "}
                    </p>
                    <p>
                        {" "}
                        Tot disco MH %:{" "}
                        <span className="bold">
                            {props.listModule &&
                                props.computeTotDiscoAndPerc((sku) => sku.mustHave).perc}
                        </span>{" "}
                    </p>
                    <p>
                        {" "}
                        Tot disco EL %:{" "}
                        <span className="bold">
                            {props.listModule &&
                                props.computeTotDiscoAndPerc((sku) => sku.eligible).perc}
                        </span>{" "}
                    </p>
                    <p>
                        {" "}
                        Tot disco MH+EL %:{" "}
                        <span className="bold">
                            {props.listModule &&
                                props.computeTotDiscoAndPerc((sku) => sku.eligible || sku.mustHave).perc}
                        </span>{" "}
                    </p>
                    </>
                    */
                }
                <PopupLia
                    setLiaListEdited={props.setLiaListEdited}
                    clusterLias={props.cluster.lias}
                    setClusterLias={props.setClusterLias}
                    clusterId={props.cluster.clusterId}
                />
                <PopupClusterClassification
                    classificationList={props.cluster.classifications}
                    addClassification={props.addClassification}
                    clusterId={props.cluster.clusterId}
                />
            </div>
            {props.percObj ? (
                <p>
                    <span className="bold">
                        {formatNumber(props.percObj.totalSku)}
                    </span>{" "}
                    sku{" "}
                </p>
            ) : null}
        </div>
    )
}

export default ModuleListAreaContainer;
